import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { UserEntity, parseEntityRef } from '@backstage/catalog-model';
import { InfoCard, Link } from '@backstage/core-components';

/**
 * Reverse-lookup card: shows users whose `user/manager` annotation points at
 * the current user. Always renders so the surrounding Grid layout stays
 * balanced — empty case shows an italic "No direct reports." message.
 */

const useStyles = makeStyles(theme => ({
  card: { height: '100%' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
  },
  divider: { margin: 0 },
  meta: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  empty: {
    fontStyle: 'italic',
    color: theme.palette.text.disabled,
    textAlign: 'center',
    padding: theme.spacing(2),
  },
}));

interface DirectReport {
  ref: string;
  name: string;
  role?: string;
  picture?: string;
}

function entityRefToCatalogPath(ref: string): string {
  const { kind, namespace, name } = parseEntityRef(ref, {
    defaultKind: 'user',
    defaultNamespace: 'default',
  });
  return `/catalog/${namespace.toLowerCase()}/${kind.toLowerCase()}/${name}`;
}

export function EntityUserDirectReportsCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const userRef = `user:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;

  const [reports, setReports] = useState<DirectReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Reset state immediately so a userRef change doesn't show the previous
    // user's reports (or a stale title count) while the new fetch is pending.
    setReports([]);
    setLoading(true);
    let cancelled = false;
    // Filter on annotation value directly — Backstage's catalog filter
    // supports dot-notation paths into metadata.annotations.
    catalogApi
      .getEntities({
        filter: {
          kind: 'User',
          ['metadata.annotations.user/manager']: userRef,
        },
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.annotations',
          'spec.profile',
        ],
      })
      .then(res => {
        if (cancelled) return;
        const rows: DirectReport[] = (res.items as UserEntity[]).map(u => ({
          // Honour the entity's actual namespace rather than hard-coding default.
          ref: `user:${u.metadata.namespace ?? 'default'}/${u.metadata.name}`,
          name: u.spec?.profile?.displayName ?? u.metadata.name,
          role: u.metadata.annotations?.['user/role'],
          picture: u.spec?.profile?.picture,
        }));
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setReports(rows);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, userRef]);

  let body: JSX.Element;
  if (loading) {
    body = (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (reports.length === 0) {
    body = (
      <Typography className={classes.empty}>No direct reports.</Typography>
    );
  } else {
    body = (
      <>
        {reports.map((r, idx) => (
          <Box key={r.ref}>
            {idx > 0 && <Divider className={classes.divider} />}
            <Box className={classes.row}>
              <Avatar src={r.picture} style={{ width: 36, height: 36 }}>
                {r.name.charAt(0)}
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Link to={entityRefToCatalogPath(r.ref)}>{r.name}</Link>
                {r.role && (
                  <Typography className={classes.meta} component="div">
                    {r.role}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </>
    );
  }

  const title =
    reports.length > 0 ? `Direct reports (${reports.length})` : 'Direct reports';

  return (
    <InfoCard title={title} variant="gridItem">
      {body}
    </InfoCard>
  );
}
